'use client';

import React, { useRef, useState, useCallback, useMemo } from 'react';
import { compressImage } from '@/lib/compressImage';
import { useCamera, CameraModal } from '@/hooks/useCamera';
import type { FieldValue } from '../formFieldTypes';

/**
 * PhotoCaptureField — Camera-based photo capture with GPS/timestamp overlay
 *
 * Uses the rich useCamera/CameraModal hook for torch, zoom, focus, and review.
 * Falls back to a file input when the camera is unavailable or on desktop.
 *
 * options.min_photos       — Minimum required photos
 * options.max_photos       — Maximum allowed photos
 * options.require_timestamp — Overlay timestamp on photo
 * options.require_gps      — Capture GPS with photo
 * options.photo_instructions — Display instructions to the user
 * options.quality           — 'low' | 'medium' | 'high'
 */

type PhotoCaptureOptions = {
      min_photos?: number;
      max_photos?: number;
      require_timestamp?: boolean;
      require_gps?: boolean;
      photo_instructions?: string;
      aspect_ratio?: '4:3' | '16:9' | '1:1';
      quality?: 'low' | 'medium' | 'high';
      [key: string]: unknown;
};

interface PhotoCaptureFieldProps {
  field: {
    key: string;
    label: string;
    required?: boolean;
    readonly?: boolean;
    options?: unknown;
    help_text?: string;
    [key: string]: unknown;
  };
  value: FieldValue; // Array of { data_url, timestamp?, gps?, caption? }
  onChange: (value: FieldValue) => void;
  isDark: boolean;
}

interface CapturedPhoto {
  data_url: string;
  timestamp: string;
  type?: 'before' | 'during' | 'after';
  gps?: { lat: number; lng: number; accuracy: number };
  caption?: string;
  file_name?: string;
}

type PhotoPhase = 'before' | 'during' | 'after';

const PHOTO_PHASES: Array<{ key: PhotoPhase; label: string; emoji: string }> = [
  { key: 'before', label: 'Before', emoji: '📋' },
  { key: 'during', label: 'During', emoji: '🔧' },
  { key: 'after', label: 'After', emoji: '✅' },
];

export const PhotoCaptureField: React.FC<PhotoCaptureFieldProps> = ({ field, value, onChange, isDark }) => {
  const opts = (field.options ?? {}) as PhotoCaptureOptions;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [capturingGps, setCapturingGps] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState<PhotoPhase>('during');
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState(false);

  const photos = useMemo<CapturedPhoto[]>(() => Array.isArray(value) ? (value as CapturedPhoto[]) : (value ? [value as unknown as CapturedPhoto] : []), [value]);
  const maxPhotos = opts?.max_photos ?? 10;
  const minPhotos = opts?.min_photos ?? 0;
  const requireGps = opts?.require_gps ?? false;
  const instructions = opts?.photo_instructions;
  const canAddMore = photos.length < maxPhotos && !field.readonly;

  const qualityMap = { low: 0.5, medium: 0.7, high: 0.9 };
  const quality = qualityMap[opts?.quality || 'medium'];
  const maxDim = opts?.quality === 'high' ? 2048 : opts?.quality === 'low' ? 800 : 1280;

  const captureGps = useCallback((): Promise<{ lat: number; lng: number; accuracy: number } | undefined> => {
    if (!requireGps || !navigator.geolocation) return Promise.resolve(undefined);
    setCapturingGps(true);
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setCapturingGps(false);
          resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
        },
        () => {
          setCapturingGps(false);
          resolve(undefined);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  }, [requireGps]);

  const addPhoto = useCallback(async (base64: string) => {
    if (photos.length >= maxPhotos) return;
    const gps = await captureGps();
    // Compress the captured image
    const compressed = await compressImage(base64, maxDim, quality);
    const newPhoto: CapturedPhoto = {
      data_url: compressed,
      timestamp: new Date().toISOString(),
      type: selectedPhase,
      gps,
    };
    onChange([...photos, newPhoto]);
  }, [photos, maxPhotos, quality, maxDim, captureGps, onChange, selectedPhase]);

  // useCamera hook — rich camera with torch, zoom, focus, review
  const camera = useCamera({
    onCapture: (base64: string) => {
      void addPhoto(base64);
    },
    onClose: () => setCameraOpen(false),
    reviewCapture: true,
    initialFacingMode: 'environment',
    compressOutput: { maxDim, quality },
  });

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;

    const gps = await captureGps();
    const newPhotos: CapturedPhoto[] = [];

    for (let i = 0; i < files.length && photos.length + newPhotos.length < maxPhotos; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) continue;

      const dataUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = async () => {
          const compressed = await compressImage(reader.result as string, maxDim, quality);
          resolve(compressed);
        };
        reader.readAsDataURL(file);
      });

      newPhotos.push({
        data_url: dataUrl,
        timestamp: new Date().toISOString(),
        type: selectedPhase,
        gps,
        file_name: file.name,
      });
    }

    onChange([...photos, ...newPhotos]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [photos, maxPhotos, quality, maxDim, captureGps, onChange, selectedPhase]);

  const removePhoto = useCallback((index: number) => {
    if (field.readonly) return;
    onChange(photos.filter((_, i) => i !== index));
  }, [photos, onChange, field.readonly]);

  const updateCaption = useCallback((index: number, caption: string) => {
    const updated = [...photos];
    updated[index] = { ...updated[index], caption };
    onChange(updated);
  }, [photos, onChange]);

  const deficit = Math.max(0, minPhotos - photos.length);

  const openCaptureForPhase = useCallback((phase: PhotoPhase) => {
    setSelectedPhase(phase);
    // Try the rich camera first; fall back to file input on error
    setCameraError(false);
    setCameraOpen(true);
    try {
      camera.open();
    } catch {
      setCameraError(true);
      fileInputRef.current?.click();
    }
  }, [camera]);

  // If the camera errors out, fall back to file input
  React.useEffect(() => {
    if (cameraOpen && camera.error) {
      setCameraError(true);
      setCameraOpen(false);
      fileInputRef.current?.click();
    }
  }, [cameraOpen, camera.error]);

  return (
    <div className="space-y-2">
      {/* Instructions */}
      {instructions && (
        <p className={`text-xs italic px-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          📷 {instructions}
        </p>
      )}

      {/* Photo grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((photo, idx) => (
            <div key={idx} className={`relative group rounded-lg overflow-hidden border ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
              <img
                src={photo.data_url}
                alt={photo.caption || `Photo ${idx + 1}`}
                className="w-full h-24 object-cover"
              />
              {photo.type && (
                <div className="absolute top-1 left-1 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-semibold uppercase text-white">
                  {photo.type}
                </div>
              )}
              {/* Timestamp overlay */}
              {photo.timestamp && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[9px] px-1 py-0.5 truncate">
                  {new Date(photo.timestamp).toLocaleString()}
                  {photo.gps && ` • GPS ✓`}
                </div>
              )}
              {/* Remove button */}
              {!field.readonly && (
                <button
                  type="button"
                  onClick={() => removePhoto(idx)}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Capture button */}
      {canAddMore && (
        <div>
          {/* Hidden file input — fallback for desktop or camera errors */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple={maxPhotos - photos.length > 1}
            onChange={handleFileSelect}
            className="hidden"
          />
          {capturingGps ? (
            <div className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed text-sm font-medium ${
              isDark
                ? 'border-gray-600 text-gray-400 bg-gray-900/30'
                : 'border-gray-300 text-gray-500 bg-gray-50'
            }`}>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
              Acquiring GPS...
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {PHOTO_PHASES.map((phase) => (
                <button
                  key={phase.key}
                  type="button"
                  onClick={() => openCaptureForPhase(phase.key)}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed text-sm font-medium transition-colors ${
                    isDark
                      ? 'border-gray-600 text-gray-300 hover:border-teal-500 hover:text-teal-400 hover:bg-teal-950/20'
                      : 'border-gray-300 text-gray-600 hover:border-teal-500 hover:text-teal-600 hover:bg-teal-50'
                  }`}
                >
                  <span>{phase.emoji}</span>
                  <span>{phase.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Rich Camera Modal — torch, zoom, focus, review */}
      {cameraOpen && !cameraError && (
        <CameraModal
          camera={camera}
          title={`Capture Photo — ${selectedPhase}`}
          reviewCapture
          onAfterCapture={() => {
            // Camera stays open for review; close after submit
          }}
        />
      )}

      {/* Min photos deficit warning */}
      {deficit > 0 && (
        <p className={`text-xs ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
          {deficit} more photo{deficit !== 1 ? 's' : ''} required
        </p>
      )}

      {/* Count display */}
      {photos.length > 0 && (
        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          {photos.length} of {maxPhotos} photos{minPhotos > 0 ? ` (min ${minPhotos})` : ''}
        </p>
      )}
    </div>
  );
};

export default PhotoCaptureField;
